import aaTrackEvent from './utils/aaTrackEvent';

/**
 * A custom element in charge of adding base JS functionality to the header.
 * Examples: all header tracking, updating header content when a BBB changes.
 *
 * @module iabbb-header
 */
class Header extends HTMLElement {
  connectedCallback() {
    self.addEventListener('hf:set-bbb', this.handleSetBbb);
    self.addEventListener('iabbb-country-custom-select:select', this.syncCountryCustomSelects);
    this.addAnalyticsEventListeners();
  }

  disconnectedCallback() {
    self.removeEventListener('hf:set-bbb', this.handleSetBbb);
    self.addEventListener('iabbb-country-custom-select:select', this.syncCountryCustomSelects);
    this.removeAnalyticsEventListeners();
  }

  /**
   * Emit a custom event
   * @param {string} name - The event name
   * @param {object} detail - Any details to pass along with the event
   */
  emit = (name, detail = {}) => {
    // Create a new event
    const event = new CustomEvent(name, {
      bubbles: true,
      cancelable: true,
      composed: true,
      detail: detail,
    });

    // Dispatch the event
    return this.dispatchEvent(event);
  };

  handleSetBbb = (e) => {
    this.updatePopularCategories(e.detail.popularCategories);
  };

  syncCountryCustomSelects = (e) => {
    this.emit('iabbb-country-custom-select:manual-set-option', { value: e.detail.value });
  };

  addAnalyticsEventListeners = () => {
    self.addEventListener('iabbb-locale-selector:click-location-link', this.handleClickLocaleSelectorLocationLink);

    self.addEventListener('iabbb-search-bar:execute', this.handleSearchExecute);

    self.addEventListener('iabbb-find-autocomplete:type', this.handleFindAutocompleteType);
    self.addEventListener('iabbb-near-autocomplete:type', this.handleNearAutocompleteType);

    self.addEventListener('iabbb-find-autocomplete:select-option', this.handleFindAutocompleteSelect);
    self.addEventListener('iabbb-near-autocomplete:select-option', this.handleNearAutocompleteSelect);
  };

  removeAnalyticsEventListeners = () => {
    self.removeEventListener(
      'iabbb-locale-selector:click-location-link',
      this.handleClickLocaleSelectorLocationLink,
    );

    self.removeEventListener('iabbb-search-bar:execute', this.handleSearchExecute);

    self.removeEventListener('iabbb-find-autocomplete:type', this.handleFindAutocompleteType);
    self.removeEventListener('iabbb-near-autocomplete:type', this.handleNearAutocompleteType);

    self.removeEventListener('iabbb-find-autocomplete:select-option', this.handleFindAutocompleteSelect);
    self.removeEventListener('iabbb-near-autocomplete:select-option', this.handleNearAutocompleteSelect);
  };

  updatePopularCategories = (popularCategories) => {
    window.headerFooterData = {
      ...(window.headerFooterData || {}),
      bbb: {
        ...(window.headerFooterData?.bbb || {}),
        popularCategories,
      },
    };
  };

  handleSearchExecute = () => {
    aaTrackEvent('searchCompleted');
    aaTrackEvent('searchExecuted');
  };

  handleFindAutocompleteType = (e) => {
    this.updateSearchDataLayer({
      find_query: e.detail.query,
      find_method: 'type',
      used_type_aheads: 'false',
    });
  };

  handleNearAutocompleteType = (e) => {
    this.updateSearchDataLayer({
      near_query: e.detail.query,
      near_method: 'type',
      used_type_aheads: 'false',
    });
  };

  handleFindAutocompleteSelect = (e) => {
    this.updateSearchDataLayer({
      find_query: e.detail.value,
      find_method: 'selected',
      used_type_aheads: 'true',
    });
  };

  handleNearAutocompleteSelect = (e) => {
    this.updateSearchDataLayer({
      near_query: e.detail.value,
      near_method: 'selected',
      used_type_aheads: 'true',
    });
  };

  updateSearchDataLayer = (updatedSearchDataLayer) => {
    if (window.webDigitalData?.search_info) {
      window.webDigitalData.search_info = {
        ...window.webDigitalData.search_info,
        updatedSearchDataLayer,
      };
    }
  };
}

customElements.define('iabbb-header', Header);
